import { Box } from '@mui/material';
import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useUploadMediaAsset } from '@/hooks/useUploadMediaAsset';
import { useImportView, useNodeData } from '@/components/Recipe/FlowContext';
import { useWorkflowStore } from '@/state/workflow.state';
import { FlexCol } from '@/UI/styles';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { GridViewer } from '@/components/GridViewer/GridViewer';
import { I18N_KEYS } from '@/language/keys';
import { ImportData, FileSortOrder } from '@/types/node';
import { FileKind } from '@/designer/designer';
import { NodeViewMode } from '@/enums/node-view-mode.enum';
import GalleryGradientOverlay from '@/components/Common/GalleryGradientOverlay';
import GalleryMenu from '@/components/Common/ImageList/GalleryMenu';
import { useImageActions } from '@/components/Common/ImageList/useImageActions';
import { FilesViewOverlay } from '@/components/Nodes/ModelComponents/FilesViewOverlay/FilesViewOverlay';
import { useIsHovered } from '@/hooks/useIsHovered';
import { useContextMenu } from '@/hooks/useContextMenu';
import { useAnalytics, TrackTypeEnum } from '@/hooks/useAnalytics';
import { useNodeActions } from '@/components/Nodes/useNodeActions';
import { UploadProgressOverlay } from '../../Common/FileUploader/UploadProgressOverlay';
import { UploadAnotherOverlay } from '../../Common/FileUploader/UploadAnotherOverlay';
import { FileViewer } from '../Shared/FileViewer';
import { NodeFooterContainer } from '../Shared/NodeFooterContainer';
import { getAssetsTypeTranslationKey, getFileViewerControlsOffset } from '../utils/nodeViewUtils';
import type { MediaAsset } from '@/types/api/assets';

export const ImportNodeViewer = ({
  id,
  files: originalFiles,
  selectedIndex,
  onUpload,
  viewMode = NodeViewMode.Single,
  selectedViewMode,
  onDownloadCurrent,
  onDownloadAll,
  sortOrder,
  onSortOrderChange,
  nodeName,
}: {
  id: string;
  files: FileKind[];
  selectedIndex: number;
  onUpload: (file: Partial<MediaAsset> | null) => void;
  viewMode?: NodeViewMode;
  selectedViewMode: NodeViewMode;
  onDownloadCurrent?: () => Promise<void>;
  onDownloadAll?: () => Promise<void>;
  sortOrder?: FileSortOrder;
  onSortOrderChange?: (sortOrder: FileSortOrder) => void;
  nodeName?: string;
}) => {
  const files = useMemo<Array<FileKind & { globalIndex: number }>>(() => {
    return originalFiles.map((file, index) => ({
      ...file,
      globalIndex: index,
    }));
  }, [originalFiles]);
  const { t } = useTranslation();
  const role = useWorkflowStore((state) => state.workflowRole);
  const importView = useImportView(id);
  const selectedFile = files[selectedIndex];
  const fileType = selectedFile?.type;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const nodeData = useNodeData<ImportData>(id);
  const { isHovered, ...hoverElementProps } = useIsHovered();
  const { setCoverImage } = useNodeActions();
  const { track } = useAnalytics();

  const {
    contextMenu,
    rightClickedItem: rightClickedFile,
    openContextMenu,
    closeContextMenu: handleContextMenuClose,
  } = useContextMenu<FileKind & { globalIndex: number }>();

  const handleSingleContextMenu = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (selectedFile) openContextMenu(e, selectedFile);
    },
    [openContextMenu, selectedFile],
  );

  const handleGridContextMenu = useCallback(
    (e: React.MouseEvent, globalIndex: number) => {
      const file = files.find((f) => f.globalIndex === globalIndex);
      if (file) openContextMenu(e, file);
    },
    [files, openContextMenu],
  );

  // GalleryMenu still operates on array indices; derive once at the boundary.
  const rightClickedIndex = useMemo(() => {
    if (!rightClickedFile) return null;
    const i = files.indexOf(rightClickedFile);
    return i >= 0 ? i : null;
  }, [rightClickedFile, files]);

  const { handleDownload, handleDownloadAll, handleCopyImage } = useImageActions({
    handleContextMenuClose,
    images: files,
    nodeName: nodeName ?? id,
  });

  const handleSetAsCover = useCallback(() => {
    if (rightClickedFile?.type === 'image') {
      track('node_image_set_as_cover_clicked', {}, TrackTypeEnum.BI);
      void setCoverImage(rightClickedFile.url);
    }
    handleContextMenuClose();
  }, [rightClickedFile, setCoverImage, track, handleContextMenuClose]);

  const handleSetViewMode = useCallback(
    (viewMode: NodeViewMode) => {
      importView.setViewMode(viewMode);
    },
    [importView],
  );

  const { dropzoneState, isInProgress, getAcceptedFileTypesToUpload } = useUploadMediaAsset({
    onUpload,
    allowMultiple: true,
    acceptedFileType: fileType,
  });

  const getAddMoreTextBasedOnCurrentFile = useCallback(() => {
    switch (selectedFile?.type) {
      case 'image':
        return t(I18N_KEYS.RECIPE_MAIN.NODES.IMPORT_NODE.ADD_MORE_IMAGES);
      case 'video':
        return t(I18N_KEYS.RECIPE_MAIN.NODES.IMPORT_NODE.ADD_MORE_VIDEOS);
      case 'audio':
        return t(I18N_KEYS.RECIPE_MAIN.NODES.IMPORT_NODE.ADD_MORE_AUDIO);
      case '3D':
        return t(I18N_KEYS.RECIPE_MAIN.NODES.IMPORT_NODE.ADD_MORE_3D_FILES);
      default:
        return t(I18N_KEYS.RECIPE_MAIN.NODES.IMPORT_NODE.ADD_MORE_FILES);
    }
  }, [selectedFile, t]);

  const handleBack = useCallback(() => {
    const steps = files.length;
    const newIndex = selectedIndex === 0 ? steps - 1 : selectedIndex - 1;
    importView.setSelectedIndex(newIndex, false);
  }, [importView, files, selectedIndex]);

  const handleNext = useCallback(() => {
    const steps = files.length;
    const newIndex = selectedIndex === steps - 1 ? 0 : selectedIndex + 1;
    importView.setSelectedIndex(newIndex, false);
  }, [importView, files, selectedIndex]);

  const handleAddMoreImages = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handle3DLockStateChange = useCallback(
    (locked: boolean) => {
      importView.setCameraLocked(locked);
    },
    [importView],
  );

  const handleFileSelect = useCallback(
    (index: number) => {
      importView.setSelectedIndex(index, false);
    },
    [importView],
  );

  const steps = files.length;
  const fileViewerControlsOffset = getFileViewerControlsOffset(viewMode, selectedFile);
  const showAddMoreSeparator =
    viewMode === NodeViewMode.Single && (selectedFile?.type === 'video' || selectedFile?.type === 'audio');

  return (
    <FlexCol sx={{ width: '100%', height: '100%', gap: showAddMoreSeparator ? 0 : 2 }}>
      <Box
        className="import-node-content"
        sx={{
          width: '100%',
          height: '100%',
          cursor: 'default',
          position: 'relative',
        }}
        {...(role === 'editor' ? dropzoneState.getRootProps() : {})}
      >
        {viewMode === NodeViewMode.Single ? (
          <Box {...hoverElementProps}>
            <FileViewer
              id={id}
              index={selectedIndex}
              asset={selectedFile}
              handleContextMenu={handleSingleContextMenu}
            />
            <GalleryGradientOverlay isSelected={isHovered} bottomOffset={fileViewerControlsOffset}>
              <FilesViewOverlay
                asset={selectedFile}
                viewMode={viewMode}
                steps={steps}
                selected={selectedIndex}
                handleBack={handleBack}
                handleNext={handleNext}
                is3DLocked={nodeData.cameraLocked ?? false}
                setIs3DLocked={handle3DLockStateChange}
                setViewMode={handleSetViewMode}
                selectedViewMode={selectedViewMode}
                onDownloadAll={onDownloadCurrent ? () => void onDownloadCurrent() : undefined}
                sortOrder={sortOrder}
                onSortOrderChange={onSortOrderChange}
              />
            </GalleryGradientOverlay>
            {isInProgress ? <UploadProgressOverlay /> : null}
            {dropzoneState.isDragActive ? (
              <UploadAnotherOverlay
                show={dropzoneState.isDragActive}
                sx={{ bottom: fileViewerControlsOffset ?? 0, borderRadius: '7px' }}
              />
            ) : null}
          </Box>
        ) : (
          <Box {...hoverElementProps}>
            <GridViewer
              nodeId={id}
              files={files}
              selectedIndex={selectedIndex}
              onFileSelect={handleFileSelect}
              onContextMenu={handleGridContextMenu}
            />
            <GalleryGradientOverlay isSelected={isHovered}>
              <FilesViewOverlay
                onDownloadAll={onDownloadAll ? () => void onDownloadAll() : undefined}
                viewMode={viewMode}
                setViewMode={handleSetViewMode}
                selectedViewMode={selectedViewMode}
                sortOrder={sortOrder}
                onSortOrderChange={onSortOrderChange}
                filesNavigationComponent={
                  <Box sx={{ p: 1 }}>
                    {files.length} {t(getAssetsTypeTranslationKey(files))}
                  </Box>
                }
              />
            </GalleryGradientOverlay>
          </Box>
        )}
      </Box>
      <GalleryMenu
        contextMenu={contextMenu}
        handleContextMenuClose={handleContextMenuClose}
        handleCopyImage={handleCopyImage}
        handleDownload={handleDownload}
        handleDownloadAll={handleDownloadAll}
        handleSetAsCover={handleSetAsCover}
        rightClickedIndex={rightClickedIndex}
        selectedFile={rightClickedFile ?? undefined}
      />
      {role === 'editor' && (
        <>
          <NodeFooterContainer showSeparator={showAddMoreSeparator}>
            <ButtonContained
              mode="text"
              size="small"
              onClick={handleAddMoreImages}
              disabled={isInProgress}
              sx={{ alignSelf: 'flex-start' }}
            >
              + {getAddMoreTextBasedOnCurrentFile()}
            </ButtonContained>
          </NodeFooterContainer>
          <input
            type="file"
            style={{ display: 'none' }}
            {...dropzoneState.getInputProps()}
            ref={fileInputRef}
            accept={getAcceptedFileTypesToUpload()}
          />
        </>
      )}
    </FlexCol>
  );
};
